import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  ThreadListPrimitive,
  ThreadListItemPrimitive,
  useAui,
  useAuiState,
} from '@assistant-ui/react'
import { useAuth } from '@/contexts/AuthContext'

interface ChatThreadListProps {
  collapsed: boolean
  onToggleCollapse: () => void
}

function isPersistedNamedThread(item: {
  remoteId?: string
  title?: string
}): boolean {
  if (!item.remoteId) return false
  const title = displayTitle(item.title)
  return title !== 'New chat'
}

function displayTitle(raw: string | undefined): string {
  if (!raw?.trim()) return 'New chat'
  const trimmed = raw.trim()
  // Guard against accidental JSON titles from assistant-ui message payloads.
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        const text = parsed
          .map((part) => {
            if (part && typeof part === 'object' && 'text' in part) {
              return String((part as { text?: unknown }).text ?? '')
            }
            return ''
          })
          .join('')
          .trim()
        return text || 'New chat'
      }
    } catch {
      // fall through
    }
  }
  return trimmed
}

function ThreadListItemRow({ collapsed }: { collapsed: boolean }) {
  const aui = useAui()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const title = useAuiState((state) =>
    displayTitle(state.threadListItem.title),
  )
  const isMain = useAuiState(
    (state) => state.threads.mainThreadId === state.threadListItem.id,
  )
  const status = useAuiState((state) => state.threadListItem.status)

  async function handleSelect() {
    if (location.pathname !== '/') {
      navigate('/')
    }
  }

  async function handleRename() {
    const nextTitle = window.prompt('Rename chat', title)
    if (!nextTitle?.trim()) return
    await aui.threadListItem().rename(nextTitle.trim())
    setMenuOpen(false)
  }

  async function handleArchive() {
    if (status === 'archived') {
      await aui.threadListItem().unarchive()
    } else {
      await aui.threadListItem().archive()
    }
    setMenuOpen(false)
  }

  async function handleDelete() {
    const confirmed = window.confirm('Delete this chat permanently?')
    if (!confirmed) return
    await aui.threadListItem().delete()
    setMenuOpen(false)
  }

  if (collapsed) {
    return (
      <ThreadListItemPrimitive.Root className="group relative">
        <ThreadListItemPrimitive.Trigger
          onClick={() => void handleSelect()}
          title={title}
          className={`flex w-full items-center justify-center rounded-lg px-2 py-2 text-sm transition ${
            isMain
              ? 'bg-gray-300 text-gray-900'
              : 'text-gray-700 hover:bg-gray-200'
          }`}
        >
          <span className="text-xs font-semibold uppercase">
            {title.slice(0, 1)}
          </span>
        </ThreadListItemPrimitive.Trigger>
      </ThreadListItemPrimitive.Root>
    )
  }

  return (
    <ThreadListItemPrimitive.Root className="group relative">
      <ThreadListItemPrimitive.Trigger
        onClick={() => void handleSelect()}
        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition ${
          isMain
            ? 'bg-gray-300 font-medium text-gray-900'
            : 'text-gray-700 hover:bg-gray-200'
        }`}
      >
        <span className="truncate">{title}</span>
      </ThreadListItemPrimitive.Trigger>

      <div className="absolute right-1 top-1/2 -translate-y-1/2">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            setMenuOpen((open) => !open)
          }}
          className="rounded px-1.5 py-0.5 text-xs text-gray-500 opacity-0 transition group-hover:opacity-100 hover:bg-gray-200 hover:text-gray-700"
        >
          ···
        </button>
        {menuOpen ? (
          <div className="absolute right-0 z-10 mt-1 min-w-32 rounded-md border border-gray-200 bg-white p-1 shadow-md">
            <button
              type="button"
              onClick={() => void handleRename()}
              className="block w-full rounded px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              Rename
            </button>
            <button
              type="button"
              onClick={() => void handleArchive()}
              className="block w-full rounded px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              {status === 'archived' ? 'Unarchive' : 'Archive'}
            </button>
            <div className="my-1 h-px bg-gray-100" />
            <button
              type="button"
              onClick={() => void handleDelete()}
              className="block w-full rounded px-2 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        ) : null}
      </div>
    </ThreadListItemPrimitive.Root>
  )
}

export default function ChatThreadList({
  collapsed,
  onToggleCollapse,
}: ChatThreadListProps) {
  const { user, logout } = useAuth()
  const aui = useAui()
  const navigate = useNavigate()
  const location = useLocation()
  const threadIds = useAuiState((state) => state.threads.threadIds)
  const threadItems = useAuiState((state) => state.threads.threadItems)

  const visibleThreadIds = useMemo(() => {
    return threadIds.filter((threadId) => {
      try {
        const item = aui.threads().item({ id: threadId }).getState()
        return isPersistedNamedThread(item)
      } catch {
        return false
      }
    })
  }, [aui, threadIds, threadItems])

  function goHome() {
    if (location.pathname !== '/') {
      navigate('/')
    }
  }

  async function handleNewChat() {
    if (location.pathname !== '/') {
      navigate('/')
    }
  }

  const newChatClassName = collapsed
    ? 'flex w-full items-center justify-center rounded-lg px-2 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200'
    : 'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-200'

  return (
    <aside className="flex h-full w-full flex-col border-r border-gray-200 bg-gray-50">
      <div
        className={`shrink-0 flex items-center border-b border-gray-200 ${
          collapsed ? 'justify-center px-2 py-3' : 'justify-between px-4 py-3'
        }`}
      >
        {collapsed ? (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="flex h-8 w-8 items-center justify-center rounded text-gray-500 transition hover:bg-gray-200 hover:text-gray-800"
            title="Expand sidebar"
            aria-label="Expand sidebar"
          >
            <ChevronRightIcon />
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={goHome}
              className="text-left text-xl font-bold leading-7 text-gray-900"
            >
              Inquisitor
            </button>
            <button
              type="button"
              onClick={onToggleCollapse}
              className="rounded p-1 text-gray-400 transition hover:bg-gray-200 hover:text-gray-700"
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
            >
              <ChevronLeftIcon />
            </button>
          </>
        )}
      </div>

      {/* Fixed above the scrollable thread list */}
      <div className="shrink-0 px-2 pt-2">
        <ThreadListPrimitive.New
          onClick={() => void handleNewChat()}
          className={newChatClassName}
          title="New chat"
        >
          {collapsed ? (
            <PlusIcon />
          ) : (
            <>
              <PlusIcon />
              <span>New chat</span>
            </>
          )}
        </ThreadListPrimitive.New>
      </div>

      <ThreadListPrimitive.Root className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-2 pt-1">
        {!collapsed ? (
          <h3 className="sticky top-0 z-[1] bg-gray-50 px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-400">
            Chats
          </h3>
        ) : null}
        <div className="space-y-0.5">
          {visibleThreadIds.map((threadId) => {
            const index = threadIds.indexOf(threadId)
            if (index < 0) return null
            return (
              <ThreadListPrimitive.ItemByIndex
                key={threadId}
                index={index}
                components={{
                  ThreadListItem: () => (
                    <ThreadListItemRow collapsed={collapsed} />
                  ),
                }}
              />
            )
          })}
        </div>
      </ThreadListPrimitive.Root>

      <div className={`shrink-0 ${collapsed ? 'p-2' : 'p-6'}`}>
        <div
          className={`border-t border-gray-200 ${collapsed ? 'mb-2' : 'mb-3'}`}
        />

        {user?.is_super_admin ? (
          <Link
            to="/settings"
            className={`flex items-center rounded-lg py-3 min-h-[44px] transition-colors ${
              collapsed ? 'justify-center px-2' : 'px-4'
            } ${
              location.pathname === '/settings'
                ? 'font-medium text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            style={
              location.pathname === '/settings'
                ? { background: 'var(--gradient-primary)' }
                : undefined
            }
            title={collapsed ? 'Settings' : undefined}
          >
            <CogIcon className={`h-5 w-5 ${collapsed ? 'mr-0' : 'mr-3'}`} />
            <span className={`whitespace-nowrap ${collapsed ? 'hidden' : ''}`}>
              Settings
            </span>
          </Link>
        ) : null}

        <div
          className={`border-t border-gray-200 ${collapsed ? 'my-2' : 'my-3'}`}
        />

        {user ? (
          <div
            className={`mb-2 flex items-center gap-3 py-3 min-h-[44px] ${
              collapsed ? 'justify-center px-2' : 'px-4'
            }`}
            title={collapsed ? user.email : undefined}
          >
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-200">
              <span className="text-[10px] font-medium text-gray-600">
                {(user.name?.trim().charAt(0) || user.email.charAt(0)).toUpperCase()}
              </span>
            </div>
            <span
              className={`truncate text-sm font-medium text-gray-700 ${
                collapsed ? 'hidden' : ''
              }`}
            >
              {user.email}
            </span>
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => void logout()}
          className={`flex w-full items-center rounded-lg py-3 min-h-[44px] text-red-600 transition-colors hover:bg-red-50 ${
            collapsed ? 'justify-center px-2' : 'px-4'
          }`}
          title={collapsed ? 'Logout' : undefined}
        >
          <LogoutIcon className={`h-5 w-5 ${collapsed ? 'mr-0' : 'mr-3'}`} />
          <span className={`whitespace-nowrap ${collapsed ? 'hidden' : ''}`}>
            Logout
          </span>
        </button>
      </div>
    </aside>
  )
}

/** Heroicons outline Cog6Tooth — same path Bookkeeper uses. */
function CogIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a7.723 7.723 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 0 1 0-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </svg>
  )
}

function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
      />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg
      className="h-5 w-5 shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.5v15m7.5-7.5h-15"
      />
    </svg>
  )
}

function ChevronLeftIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  )
}
